import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import { Badge, Button, Icon, SegmentedControl, useToast } from '../components/ui';
import { AI_JOB_STATUS_LABEL, aiJobStatusTone } from '../lib/aiJobLabels';
import { formatRelativeDate } from '../utils/dateUtils';
import PiecePreview from '../components/content/PiecePreview';
import PieceEditor from '../components/content/PieceEditor';
import PieceSource from '../components/content/PieceSource';
import PieceSeo from '../components/content/PieceSeo';
import {
  featuredContext,
  hasPendingSlots,
  insertSlotMarker,
  placeSlotImage,
  removeSlotMarker,
  slotAnchor,
  slotContext,
  type ArticleBlock,
} from '../components/content/articleHtml';
import { fetchArticleDeploymentKb, type ClientKbFile } from '../lib/kb';
import {
  canRequestImages,
  createImages,
  deployGate,
  deployPiece,
  FEATURED_SLOT_KEY,
  fetchImages,
  fetchPiece,
  insertImageSlot,
  isUnresolvedImage,
  newSlotKey,
  PIECE_STATUS_LABEL,
  pieceStatusTone,
  removeImageSlot,
  selectImageOption,
  setFeaturedImage,
  submitManualPrompt,
  subscribePiece,
  updatePieceHtml,
  updatePieceStatus,
  type ContentImage,
  type ContentPieceDetail,
} from '../lib/contentPieces';

/**
 * /content/pieces/:pieceId — the full-screen article workspace.
 *
 * Preview  renders the html with the image tools (insert slots, pick options)
 * Edit     TipTap over the same html, autosaved
 * Source   HTML / markdown, copy, Download HTML
 * SEO      pre-publish checks, measured from the live html on every render
 */

type Mode = 'preview' | 'edit' | 'source' | 'seo';

export default function ContentPiece() {
  const { pieceId } = useParams<{ pieceId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [piece, setPiece] = useState<ContentPieceDetail | null>(null);
  const [images, setImages] = useState<ContentImage[]>([]);
  const [html, setHtml] = useState('');
  const [mode, setMode] = useState<Mode>('preview');
  const [editorKey, setEditorKey] = useState(0);
  const [insertMode, setInsertMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** undefined = not looked up yet, null = this client has no playbook. */
  const [deployKb, setDeployKb] = useState<ClientKbFile | null | undefined>(undefined);

  const load = useCallback(
    async (opts: { keepHtml?: boolean } = {}) => {
      if (!pieceId) return;
      try {
        const [row, imgs] = await Promise.all([fetchPiece(pieceId), fetchImages(pieceId)]);
        setPiece(row);
        setImages(imgs);
        if (row && !opts.keepHtml) setHtml(row.html);
        setError(null);
      } catch (err) {
        console.error('Error loading content piece:', err);
        setError(err instanceof Error ? err.message : 'Failed to load this piece');
      } finally {
        setLoading(false);
      }
    },
    [pieceId]
  );

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Options landing from an image job, or a status change, refresh the screen.
  // In Edit the editor owns the body, so only the rows refresh.
  useEffect(() => {
    if (!pieceId) return;
    return subscribePiece(pieceId, () => load({ keepHtml: mode === 'edit' }));
  }, [pieceId, load, mode]);

  // The client's Article Deployment playbook — the thing that unlocks Deploy.
  // Re-runs if the piece lands on a different client.
  const clientId = piece?.client_id ?? null;
  useEffect(() => {
    if (!clientId) {
      setDeployKb(null);
      return;
    }
    let cancelled = false;
    setDeployKb(undefined);
    fetchArticleDeploymentKb(clientId)
      .then((row) => {
        if (!cancelled) setDeployKb(row);
      })
      .catch((err) => {
        console.error('Error loading the Article Deployment knowledge file:', err);
        if (!cancelled) setDeployKb(null);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const featured = useMemo(() => images.find((i) => i.kind === 'featured') ?? null, [images]);
  const queued = useMemo(() => images.filter((i) => i.status === 'queued'), [images]);
  const unresolved = useMemo(() => images.filter(isUnresolvedImage), [images]);
  const jobStatus = piece?.job?.status ?? null;
  const canEditSlots = !!piece && piece.status !== 'approved' && piece.status !== 'published';
  const canCreate = queued.length > 0 && canRequestImages(jobStatus);
  const createBlocked =
    queued.length === 0
      ? 'Mark at least one image slot first — turn on Insert image and click a gutter.'
      : canRequestImages(jobStatus)
        ? null
        : `The job is ${jobStatus ? AI_JOB_STATUS_LABEL[jobStatus] : 'not on the queue'}, which can't be sent back for images.`;
  const deployBlocked = deployGate(piece, images, deployKb).reason;

  const run = async (label: string, fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      console.error(`${label} failed:`, err);
      toast.error(`${label} didn't go through`, err instanceof Error ? err.message : 'Try again.');
      await load({ keepHtml: mode === 'edit' });
    } finally {
      setBusy(false);
    }
  };

  /** images_pending → ready once every slot is picked; nothing else moves automatically. */
  const settleStatus = async (current: ContentPieceDetail, nextImages: ContentImage[]) => {
    const pending = nextImages.some(isUnresolvedImage);
    if (!pending && current.status === 'images_pending') {
      await updatePieceStatus(current.id, 'ready');
    }
  };

  const saveHtml = async (next: string) => {
    if (!piece) return;
    setHtml(next);
    await updatePieceHtml(piece.id, next);
  };

  const handleInsertAfter = (afterBlock: number, blocks: ArticleBlock[]) =>
    run('Adding the image slot', async () => {
      if (!piece) return;
      const slot_key = newSlotKey();
      await insertImageSlot({
        piece_id: piece.id,
        slot_key,
        kind: 'inline',
        anchor: slotAnchor(blocks, afterBlock),
        context_excerpt: slotContext(blocks, afterBlock),
      });
      await saveHtml(insertSlotMarker(html, afterBlock, slot_key));
      await load({ keepHtml: true });
      toast.success('Image slot added', 'Sent with the next “Create images”.');
    });

  const handleAddFeatured = () =>
    run('Adding the featured image', async () => {
      if (!piece) return;
      const row = await insertImageSlot({
        piece_id: piece.id,
        slot_key: FEATURED_SLOT_KEY,
        kind: 'featured',
        anchor: { after_block: -1, after_text: '' },
        context_excerpt: featuredContext(piece.title, html),
      });
      await setFeaturedImage(piece.id, row.id);
      await load({ keepHtml: true });
      toast.success('Featured image slot added', 'Prompt comes from the title and the opening.');
    });

  const handleRemove = (image: ContentImage) =>
    run('Removing the slot', async () => {
      if (!piece) return;
      await removeImageSlot(image.id);
      if (image.kind === 'featured') {
        await setFeaturedImage(piece.id, null);
      } else {
        await saveHtml(removeSlotMarker(html, image.slot_key));
      }
      const rest = images.filter((i) => i.id !== image.id);
      await settleStatus(piece, rest);
      await load({ keepHtml: true });
      toast.success(image.kind === 'featured' ? 'Featured image removed' : 'Image slot removed');
    });

  const handleSelect = (image: ContentImage, url: string) =>
    run('Picking that image', async () => {
      if (!piece) return;
      const updated = await selectImageOption(image.id, url);
      if (image.kind === 'inline') {
        // Alt text comes from the prompt that drew THIS option; older options have
        // none and fall back to the slot prompt, then the piece title.
        const chosen = updated.options.find((o) => o.url === url);
        await saveHtml(placeSlotImage(html, image.slot_key, url, chosen?.prompt || updated.prompt || piece.title));
      }
      const next = images.map((i) => (i.id === updated.id ? updated : i));
      await settleStatus(piece, next);
      await load({ keepHtml: true });
      toast.success(image.kind === 'featured' ? 'Featured image set' : 'Image placed');
    });

  const handleManualPrompt = (image: ContentImage, prompt: string) =>
    run('Re-queuing the slot', async () => {
      await submitManualPrompt(image.id, prompt);
      await load({ keepHtml: true });
      toast.success('Slot re-queued with your prompt', 'Click “Create images” to send it to the build queue.');
    });

  const handleCreateImages = () =>
    run('Sending to the build queue', async () => {
      if (!piece) return;
      const result = await createImages(piece, images);
      setInsertMode(false);
      await load({ keepHtml: true });
      const n = result.slots.length;
      toast.success(`Sent to the build queue · ${n} slot${n === 1 ? '' : 's'}`, 'Grok Imagine makes three options per slot.');
    });

  const handleDeploy = () =>
    run('Sending this article to deploy', async () => {
      if (!piece || !deployKb) return;
      await deployPiece(piece, deployKb);
      await load({ keepHtml: true });
      toast.success(
        'Queued for deploy',
        `The build agent follows “${deployKb.title}” to get it into ${piece.client_name || 'the client'}’s CMS.`
      );
    });

  const handleEditorSave = (next: string) =>
    run('Saving the article', async () => {
      await saveHtml(next);
      toast.success('Article saved');
    });

  const switchMode = (next: Mode) => {
    if (next === 'edit') setEditorKey((k) => k + 1);
    if (next !== 'preview') setInsertMode(false);
    setMode(next);
  };

  const back = () => navigate('/content?tab=pieces');

  return (
    <div className="min-h-screen">
      <Sidebar />
      <main className="px-4 md:px-8 pb-24 md:pb-8 md:ml-[72px]">
        <PageHeader title="Content" />

        {/* piece detail row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            paddingBottom: '14px',
            marginBottom: '18px',
            borderBottom: '1px solid var(--border-hairline)',
          }}
        >
          <button
            type="button"
            // 40px on phones: it's the only way back to the list
            className="pressable hover-wash w-10 h-10 md:w-8 md:h-8"
            onClick={back}
            aria-label="Back to pieces"
            title="Back to Content → Pieces"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-hairline)',
              background: 'var(--surface-raised)',
              color: 'var(--text-muted)',
              cursor: 'pointer',
            }}
          >
            <Icon name="chevron-left" size={16} />
          </button>
          <div style={{ flex: 1, minWidth: '240px' }}>
            <div className="t-eyebrow" style={{ marginBottom: '3px' }}>
              {piece?.client_name ? `Content · ${piece.client_name}` : 'Content'}
            </div>
            {/* h2, not h1: PageHeader above owns the page's single h1 now. Styling is
                inline, so the demotion changes nothing visually. */}
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--text-xl)',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'var(--text-body)',
                lineHeight: 1.2,
              }}
            >
              {piece?.title || (loading ? 'Loading…' : 'Untitled')}
            </h2>
          </div>
          {piece ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {piece.job ? (
                <Badge tone={aiJobStatusTone(piece.job.status)} dot>
                  Job · {AI_JOB_STATUS_LABEL[piece.job.status]}
                </Badge>
              ) : null}
              <Badge tone={pieceStatusTone(piece.status)} dot pulse={piece.status === 'images_pending'}>
                {PIECE_STATUS_LABEL[piece.status]}
              </Badge>
              <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                v{piece.version} · {formatRelativeDate(piece.updated_at)}
              </span>
              {/* The title lives on the wrapper, not the button: a disabled button
                  swallows its own hover, so the reason would never be readable. */}
              <span title={deployBlocked ?? undefined} style={{ display: 'inline-flex' }}>
                <Button
                  size="sm"
                  icon="rocket"
                  onClick={handleDeploy}
                  disabled={busy || !!deployBlocked}
                  title={deployBlocked ?? undefined}
                >
                  Deploy
                </Button>
              </span>
            </div>
          ) : null}
          <SegmentedControl
            options={[
              { id: 'preview', label: 'Preview' },
              { id: 'edit', label: 'Edit' },
              { id: 'source', label: 'Source' },
              { id: 'seo', label: 'SEO' },
            ]}
            value={mode}
            onChange={(id) => switchMode(id as Mode)}
          />
        </div>

        {error ? (
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--danger-400)' }}>{error}</p>
        ) : loading && !piece ? (
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>Loading piece…</p>
        ) : !piece ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-start' }}>
            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
              No content piece with that id.
            </p>
            <Button variant="secondary" icon="chevron-left" onClick={back}>
              Back to pieces
            </Button>
          </div>
        ) : (
          <>
            {mode === 'preview' ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap',
                  marginBottom: '20px',
                }}
              >
                {canEditSlots ? (
                  <Button
                    size="sm"
                    variant={insertMode ? 'primary' : 'secondary'}
                    icon={insertMode ? 'check' : 'plus'}
                    onClick={() => setInsertMode((v) => !v)}
                    disabled={busy}
                  >
                    {insertMode ? 'Done inserting' : 'Insert image'}
                  </Button>
                ) : null}
                {/* Same wrapper trick as Deploy: a disabled button swallows its own
                    hover, so a title on the button itself is never readable — which
                    is how this went dead with no explanation at all. */}
                <span title={createBlocked ?? undefined} style={{ display: 'inline-flex' }}>
                  <Button
                    size="sm"
                    icon="zap"
                    onClick={handleCreateImages}
                    disabled={busy || !canCreate}
                    title={createBlocked ?? undefined}
                  >
                    {busy ? 'Working…' : `Create images${queued.length ? ` (${queued.length})` : ''}`}
                  </Button>
                </span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', lineHeight: 1.5 }}>
                  {insertMode
                    ? 'Click a gutter between paragraphs to mark where an image goes.'
                    : unresolved.length > 0
                      ? `${unresolved.length} slot${unresolved.length === 1 ? '' : 's'} still need${unresolved.length === 1 ? 's' : ''} a pick.`
                      : hasPendingSlots(html)
                        ? 'A marker in the body has no slot row; remove it in Edit.'
                        : images.length > 0
                          ? 'Every image is placed. Approve the build on the Agents screen when ready.'
                          : 'No images yet. Turn on Insert image to mark spots, or add a featured image.'}
                </span>
              </div>
            ) : null}

            {mode === 'preview' ? (
              <PiecePreview
                piece={piece}
                html={html}
                images={images}
                featured={featured}
                insertMode={insertMode}
                busy={busy}
                canEditSlots={canEditSlots}
                onInsertAfter={handleInsertAfter}
                onAddFeatured={handleAddFeatured}
                onRemove={handleRemove}
                onSelect={handleSelect}
                onManualPrompt={handleManualPrompt}
              />
            ) : mode === 'edit' ? (
              <PieceEditor key={editorKey} html={html} disabled={!canEditSlots} onSave={handleEditorSave} />
            ) : mode === 'source' ? (
              <PieceSource piece={piece} html={html} featured={featured} />
            ) : (
              <PieceSeo piece={piece} html={html} />
            )}
          </>
        )}
      </main>
    </div>
  );
}
